import makeApiCall from "./master/axiosIndex";

// Manpower Norms master — a FLAT rate card (one row per manpower type). Full
// parity with the Materials norm master (CRUD + Excel upload + async export +
// history), but with no material lines and no apply-to-task engine. Rows are
// editable (unlike immutable material norm lines).
export const manpowerApis = {
  create: (data: {
    itemCode: string;
    itemDescription?: string;
    uomId?: number | null;
    pucFactor?: number;
    unitPrice?: number | null;
    // Scope of the new row: category (PRW/OTHERS) + projectId (0 = org master).
    // Both are immutable once created, so they ride on create only.
    category?: "PRW" | "OTHERS";
    projectId?: number;
  }): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/create", method: "post", data });
  },
  update: (data: {
    manpowerNormId: number;
    itemCode?: string;
    itemDescription?: string;
    uomId?: number | null;
    pucFactor?: number;
    unitPrice?: number | null;
  }): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/update", method: "put", data });
  },
  activate: (manpowerNormId: any): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/activate",
      method: "patch",
      data: { manpowerNormId },
    });
  },
  deactivate: (manpowerNormId: any): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/deactivate",
      method: "patch",
      data: { manpowerNormId },
    });
  },
  getById: (manpowerNormId: any): Promise<any> => {
    return makeApiCall({
      url: `/manpower-norms/id/${manpowerNormId}`,
      method: "get",
    });
  },
  getDropdown: (): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/dropdown", method: "get" });
  },
  getList: (data: any): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/list", method: "get", params: data });
  },
  uploadExcel: (data: FormData): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/upload/excel",
      method: "post",
      data,
    });
  },
  // Async master export: enqueue a build job (worker builds the xlsx and stores
  // it in DMS). Returns the request reportId. The build is scoped to the given
  // projectId (0 = org master) + category (PRW/OTHERS).
  requestExport: (
    comment?: string,
    projectId?: number,
    category?: "PRW" | "OTHERS"
  ): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/export/request",
      method: "post",
      data: { comment, projectId, category },
    });
  },
  // Copies the organization-master set (projectId 0) into the given project.
  // Returns { insertedCount, message }.
  duplicateToProject: (projectId: number): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/duplicate",
      method: "post",
      data: { projectId },
    });
  },
  // Recent manpower norms export requests (poll target). Processed rows carry filePath.
  fetchExportList: (): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/export/list", method: "get" });
  },

  // ---- Apply PRW manpower to a task (crew requirement) ----
  // Applies a crew of PRW items to a task: each id → one requirement row
  // (appliedQty = round2(taskQty × pucFactor), rate = unitPrice). qty 0 is valid;
  // negatives error. Returns { applied, items, skipped }.
  applyToTask: (data: {
    taskId: number;
    manpowerNormIds: number[];
    overrideQty?: number;
  }): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/task/apply", method: "post", data });
  },
  // Removes one applied manpower item from a task (soft-delete + reason).
  removeFromTask: (data: {
    taskId: number;
    manpowerNormId: number;
    reason?: string;
  }): Promise<any> => {
    return makeApiCall({ url: "/manpower-norms/task/remove", method: "post", data });
  },
  // Flat list of PRW manpower applied to a task. Returns { items: [...] }.
  getTaskManpowerNorms: (taskId: number): Promise<any> => {
    return makeApiCall({ url: `/manpower-norms/task/${taskId}/manpower`, method: "get" });
  },
  // Read-only PRW manpower requirement rolled up per item to a hierarchy node.
  getActivityManpowerNorms: (activityId: number): Promise<any> => {
    return makeApiCall({ url: `/manpower-norms/activity/${activityId}/manpower`, method: "get" });
  },
  getPhaseManpowerNorms: (phaseId: number): Promise<any> => {
    return makeApiCall({ url: `/manpower-norms/phase/${phaseId}/manpower`, method: "get" });
  },
  getProjectManpowerNorms: (projectId: number): Promise<any> => {
    return makeApiCall({ url: `/manpower-norms/project/${projectId}/manpower`, method: "get" });
  },
  // Bulk apply-manpower-to-tasks staged upload (rows: TaskID, ItemCode, OverrideQty).
  applyManpowerExcel: (data: FormData): Promise<any> => {
    return makeApiCall({
      url: "/manpower-norms/upload/applyManpowerExcel",
      method: "post",
      data,
    });
  },
};

export default manpowerApis;
